/**
 * Turns a CRM lead into a transaction, as the signed-in agent.
 *
 * The transaction is created through the normal transaction API (so every rule that applies to a
 * hand-made transaction applies here), then the client is added as a reference party (Buyer or
 * Seller, no invitation sent), and finally the lead is linked and marked Won in the CRM.
 *
 * The CRM link is recorded as soon as the transaction exists, before the reference party, so a
 * failure later on can never leave an unlinked transaction that a retry would duplicate.
 */
import { apiClient, API_ENDPOINTS } from '../config/api';
import { getFirebaseToken } from '../utils/getFirebaseToken';
import { toE164US } from '../utils/phone';
import { crmService } from './crm.service';
import { referencePartyFor, transactionRequestFor } from '../utils/crmPipeline';
import { primaryValue } from '../utils/crm';

/**
 * Pass `existingTransactionId` to retry after the transaction was created but could not be linked:
 * nothing is created again.
 *
 * @returns {Promise<{ transactionId: string, partyAdded: boolean }>}
 * Throws if the transaction could not be created (nothing changed) or could not be linked (the
 * error has `transactionId` so the caller can say it exists and retry the link).
 */
export const convertLeadToTransaction = async ({
  book, ownerUserId, person, side, name, address, displayName, existingTransactionId,
}) => {
  const token = await getFirebaseToken();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  let transactionId = existingTransactionId;
  if (!transactionId) {
    const created = await apiClient.post(API_ENDPOINTS.transactions, transactionRequestFor(side, { name, address }), {
      headers,
      // Creating a transaction does a lot on the server; the create pages allow the same time.
      timeout: 90000,
    });
    transactionId = created.data?.id || created.data?.transactionId;
    if (!transactionId) {
      throw new Error('The transaction was created but no id came back');
    }
  }

  try {
    await crmService.linkLeadToTransaction(book, person.id, side, transactionId, ownerUserId);
  } catch (err) {
    err.transactionId = transactionId;
    throw err;
  }

  let partyAdded = true;
  try {
    await apiClient.post(
      API_ENDPOINTS.transactionParticipants(transactionId),
      referencePartyFor(side, {
        name: displayName,
        email: primaryValue(person.emails),
        phone: primaryValue(person.phones),
        toPhone: toE164US,
      }),
      { headers },
    );
  } catch (err) {
    partyAdded = false;
  }
  return { transactionId, partyAdded };
};
